import React, { useEffect, useMemo, useState } from 'react';
import {
  FormInput,
  IconPlus,
  IconRemove,
  IconSoberLiving,
  OrangeButton,
  SmallButton,
} from 'components';
import {
  addAdditionalPaymentsByManager,
  getRequestData,
} from 'api/api';
import { endpoints } from 'api/endpoints';
import { ROLE_TYPES } from 'api/constants';
import { formatPriceWithComma } from 'utils/helpers';
import { COLORS } from 'utils/colors';

const normalizeId = (value) => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const resolveInvoiceId = (source) =>
  normalizeId(source?.invoice_id ?? source?.invoiceId ?? source?.id ?? source?.invoice?.id);

const resolvePaymentId = (source) =>
  normalizeId(
    source?.payment_id ||
      source?.paymentId ||
      source?.additional_payment_id ||
      source?.additionalPaymentId ||
      source?.invoice_additional_payment_id ||
      source?.invoicePaymentId ||
      source?.invoice_payment_id ||
      source?.extra_payment_id ||
      source?.id,
  );

const getExtraPaymentKey = (payment, index) =>
  payment.id || payment.payment_id || `${payment.description || 'extra'}-${payment.payment || payment.price || 0}-${index}`;

const createEmptyAdditionalPaymentRow = () => ({ description: '', price: '' });

const renderBackendDate = (value) => {
  if (!value) return '-';
  return String(value).split('T')[0];
};

const DynamicRows = ({ invoice, onCancel, isEditMode = false }) => {
  const [invoiceFullData, setInvoiceFullData] = useState(null);
  const [rows, setRows] = useState([createEmptyAdditionalPaymentRow()]);
  const invoiceStatus = String(invoice?.status || '').toLowerCase();

  const currentInvoiceId = resolveInvoiceId(invoiceFullData) || resolveInvoiceId(invoice);

  const getInvoiceFullData = async (invoiceID) => {
    const { data } = await getRequestData(endpoints.getInvoice(invoiceID), ROLE_TYPES.slh_manager);
    setInvoiceFullData(data);
  };

  useEffect(() => {
    const invoiceID = resolveInvoiceId(invoice);
    if (!invoiceID) return;
    getInvoiceFullData(invoiceID);
  }, [invoice]);

  useEffect(() => {
    if (!isEditMode) {
      setRows([createEmptyAdditionalPaymentRow()]);
    }
  }, [isEditMode, invoice?.invoice_id]);

  const residentRows = useMemo(() => {
    const source =
      invoiceFullData?.payments ||
      invoiceFullData?.clients ||
      invoiceFullData?.residents ||
      invoiceFullData?.invoice_details ||
      invoiceFullData?.details ||
      [];

    if (!Array.isArray(source)) return [];

    return source
      .map((item) => {
        const client = item?.client || {};
        const firstName = client?.firstname || item?.firstname || '';
        const lastName = client?.lastname || item?.lastname || '';
        const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
        const name = fullName || item?.resident_name || item?.client_name || item?.name || '';
        const status = String(client?.status || item?.status || item?.client_status || '').toLowerCase();
        const week = client?.week ?? item?.week ?? null;
        const startDate =
          client?.start_date ||
          item?.start_date ||
          item?.scholarship_start ||
          item?.week_start ||
          null;
        const endDate =
          client?.end_date ||
          item?.end_date ||
          item?.scholarship_end ||
          item?.week_end ||
          null;
        const amount = Number(item?.payment ?? item?.amount ?? item?.price ?? item?.total ?? 0);

        return {
          key: item?.id || `${name}-${startDate}-${endDate}`,
          paymentID: resolvePaymentId(item),
          name,
          status,
          week,
          startDate,
          endDate,
          amount,
        };
      })
      .filter((item) => item.name);
  }, [invoiceFullData]);

  const extraPayments = useMemo(
    () => (Array.isArray(invoiceFullData?.extra_payments) ? invoiceFullData.extra_payments : []),
    [invoiceFullData],
  );

  const summary = useMemo(() => {
    const subtotal = Number(invoiceFullData?.subtotal ?? invoice?.subtotal ?? invoice?.total ?? 0);
    const tax = Number(invoiceFullData?.tax ?? invoiceFullData?.tax_total ?? 0);
    const total = Number(invoiceFullData?.total ?? invoice?.total ?? subtotal + tax);
    return { subtotal, tax, total };
  }, [invoice, invoiceFullData]);

  const partnerName =
    invoiceFullData?.partner?.name || invoice?.partner?.name || invoiceFullData?.partner_name || '';
  const partnerAddress =
    invoiceFullData?.partner?.address ||
    invoice?.partner?.address ||
    invoiceFullData?.partner_address ||
    '';

  const startDate = invoiceFullData?.week_start || invoice?.week_start;
  const endDate = invoiceFullData?.week_end || invoice?.week_end;

  const getStatusColor = (status) => {
    if (status === 'completed') return COLORS.green;
    if (status === 'in_progress') return COLORS.blue;
    if (status === 'failed' || status === 'relapse' || status === 'relapsed') return COLORS.red;
    return COLORS.darkGrey;
  };

  const addRow = () => {
    if (!isEditMode) return;
    setRows([...rows, createEmptyAdditionalPaymentRow()]);
  };

  const removeRow = (index) => {
    const updatedRows = rows.filter((_, i) => i !== index);
    setRows(updatedRows);
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const handleSave = () => {
    const preparedRows = rows.filter(
      (row) => String(row.description || '').trim() && String(row.price || '').trim(),
    );
    if (!preparedRows.length) return;

    const formData = {
      details: preparedRows,
    };

    const invoiceID = normalizeId(currentInvoiceId);
    if (!invoiceID) return;

    addAdditionalPaymentsByManager(invoiceID, formData, async () => {
      await getInvoiceFullData(invoiceID);
      setRows([createEmptyAdditionalPaymentRow()]);
      onCancel();
    });
  };

  return (
    invoiceFullData && (
      <div className="flex flex-col h-full min-h-0">
        <div className="flex flex-col gap-y-4 flex-1 min-h-0 overflow-y-auto">
          <div className="w-full rounded-lg bg-light px-4 py-3 flex items-start justify-between">
            <div className="flex items-start gap-x-2">
              <IconSoberLiving className="w-5 h-5 mt-0.5" color={COLORS.black} />
              <div>
                <p className="text-14 font-semibold uppercase">
                  {partnerName || 'Association For Sober Living'}
                </p>
                <p className="text-13 text-darkGrey">{partnerAddress || 'Some address'}</p>
              </div>
            </div>
            <div className="text-right text-13">
              <p>From: {renderBackendDate(startDate)}</p>
              <p>To: {renderBackendDate(endDate)}</p>
            </div>
          </div>

          <div className="w-full border border-divider">
            <div className="flex bg-light py-2.5 px-3 border-b border-divider">
              <p className="w-[84%] nunito-title text-base">Description</p>
              <p className="w-[16%] nunito-title text-base border-l border-divider pl-3">Amount</p>
            </div>

            {residentRows.map((item) => {
              return (
              <div key={item.key} className="flex items-center gap-x-1 py-2.5 px-3 border-b border-divider">
                <div className="w-[84%] pr-2 flex items-start justify-between gap-x-3">
                  <div>
                    <p className="text-14">{item.name}</p>
                    <div className="flex items-center gap-x-2">
                      {item.status && (
                        <p
                          className="text-12 font-medium"
                          style={{ color: getStatusColor(item.status) }}
                        >
                          {item.status
                            .replaceAll('_', ' ')
                            .replace(/\b\w/g, (char) => char.toUpperCase())}
                        </p>
                      )}
                      {(item.week || item.week === 0) && (
                        <p className="text-12 text-darkGrey">(W{item.week})</p>
                      )}
                    </div>
                  </div>
                  <div className="text-12 text-right">
                    <p>Start: {renderBackendDate(item.startDate)}</p>
                    <p>End: {renderBackendDate(item.endDate)}</p>
                  </div>
                </div>
                <p className="w-[16%] flex items-center text-14 border-l border-divider pl-3">${formatPriceWithComma(item.amount)}</p>
              </div>
              );
            })}

            {extraPayments
              .map((payment, index) => {

              return (
                <div
                  key={getExtraPaymentKey(payment, index)}
                  className="flex items-center gap-x-1 py-2.5 px-3 border-b border-divider"
                >
                  <div className="w-[84%] pr-2 flex items-center justify-between gap-x-3">
                    <p className="text-14">{payment.description}</p>
                  </div>
                  <p className="w-[16%] text-14 border-l border-divider pl-3">
                    ${formatPriceWithComma(Number(payment.payment ?? payment.price ?? 0))}
                  </p>
                </div>
              );
            })}

            {isEditMode &&
              rows.map((row, index) => (
                <div
                  className="flex py-2.5 px-3 border-b border-divider items-center gap-1"
                  key={index}
                >
                  <div className="w-[84%] pr-2 flex items-center gap-x-3">
                    <FormInput
                      className="flex-1 !mb-0"
                      placeholder="Description"
                      value={row.description}
                      readOnly={!isEditMode || invoiceStatus === 'submitted'}
                      onChange={(e) => handleInputChange(index, 'description', e.target.value)}
                    />
                  </div>
                  <FormInput
                    className="w-[16%] !mb-0"
                    placeholder="0.00"
                    type="number"
                    prefix="$"
                    value={row.price}
                    readOnly={!isEditMode || invoiceStatus === 'submitted'}
                    onChange={(e) => handleInputChange(index, 'price', e.target.value)}
                  />
                  <IconRemove
                    className="cursor-pointer"
                    onClick={() => removeRow(index)}
                  />
                </div>
              ))}

            {isEditMode && (
              <div className="w-full px-3 py-3 flex justify-center border-b border-divider">
                <span
                  onClick={addRow}
                  className={`rounded-full bg-light border border-color-divider p-2.5 cursor-pointer ${invoiceStatus === 'submitted' ? 'pointer-events-none opacity-40' : ''}`}
                >
                  <IconPlus />
                </span>
              </div>
            )}

            <div className="w-full flex justify-end">
              <div className="w-[35%] text-14 ">
                <div className="flex items-center">
                  <span className="w-[65%] px-3 py-2 text-right">Subtotal</span>
                  <span className="w-[35%] px-3 py-2 border-l border-b border-divider text-right">
                    ${formatPriceWithComma(summary.subtotal)}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="w-[65%] px-3 py-2 text-right">TAX 0%</span>
                  <span className="w-[35%] px-3 py-2 border-l border-b border-divider text-right">
                    ${formatPriceWithComma(summary.tax)}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="w-[65%] px-3 py-2 nunito-title text-base text-right">TOTAL</span>
                  <span className="w-[35%] px-3 py-2 border-l border-divider bg-light text-right nunito-title text-base">
                    ${formatPriceWithComma(summary.total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {isEditMode && (
          <div className="flex justify-end w-full border-t border-divider gap-x-6 pt-[30px] mt-auto">
            <SmallButton btnClassName="!h-11" onClick={onCancel} label="Cancel" />
            <OrangeButton
              onClick={handleSave}
              label="Save"
              disabled={
                !rows.some(
                  (row) => String(row.description || '').trim() && String(row.price || '').trim(),
                ) || invoiceStatus === 'submitted'
              }
            />
          </div>
        )}
      </div>
    )
  );
};

export default DynamicRows;
